'use client'

import { Text, TextButton } from '@macarongfactory/web-ui'
import { useRouter } from 'next/navigation'

import { getCarBrandLogoUrl, getCarHubImageUrl } from '@/entities/car'

import AiSummaryCard from './AiSummaryCard'
import CarImage from './CarImage'
import FuelTypeFilter from './FuelTypeFilter'
import RankChips from './RankChips'
import { useLockAb } from '../../_features/modoocar-lock-ab/_hooks/useLockAb'
import {
  MODOOCAR_BIKE_FALLBACK_IMAGE_URL,
  MODOOCAR_CAR_FALLBACK_IMAGE_URL,
  FUEL_DOMAIN_TO_API,
  MODOOCAR_CODE,
  useModoocarImpression,
  useModoocarTracking,
} from '../_features/modoocar'

import type { IModoocarDetail, TFuelType } from '../_features/modoocar'

interface IHeroCardProps {
  detail: IModoocarDetail
  fuelType: TFuelType
  onFuelChange: (fuel: TFuelType) => void
  onScrollTo: (sectionId: string) => void
  isInApp?: boolean
  onInAppFuelRefresh?: (fuel: TFuelType) => Promise<void>
}

const HeroCard = ({
  detail,
  fuelType,
  onFuelChange,
  onScrollTo,
  isInApp = false,
  onInAppFuelRefresh,
}: IHeroCardProps) => {
  const {
    brand,
    brandIcon,
    upperModelName,
    lowerModelName,
    segment,
    fuelOptions,
    ownerReviewCount,
    satisfactionRank,
    efficiencyRank,
    fuelCostPercentile,
    aiSummary,
    realFuel,
    isMotorcycle,
  } = detail

  const router = useRouter()
  const { sectionShow, itemClick, itemChange } = useModoocarTracking()
  const {
    data: { isLock },
  } = useLockAb()
  const heroRef = useModoocarImpression<HTMLDivElement>(() =>
    sectionShow(MODOOCAR_CODE.HERO, '차종 이미지')
  )
  const modelInfoRef = useModoocarImpression<HTMLDivElement>(() =>
    sectionShow(MODOOCAR_CODE.MODEL_INFO, '차종 정보')
  )

  const handleFuelOpen = () =>
    itemClick(MODOOCAR_CODE.MODEL_INFO, 'chip', fuelType)

  const handleFuelChange = (fuel: TFuelType) => {
    itemChange(MODOOCAR_CODE.MODEL_INFO, 'chip', fuel)
    onFuelChange(fuel)

    if (onInAppFuelRefresh) {
      onInAppFuelRefresh(fuel)
      return
    }

    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    url.searchParams.set('fuelType', FUEL_DOMAIN_TO_API[fuel])
    window.history.replaceState(null, '', `${url.pathname}${url.search}`)
    router.refresh()
  }

  const fallbackImageUrl = isMotorcycle
    ? MODOOCAR_BIKE_FALLBACK_IMAGE_URL
    : MODOOCAR_CAR_FALLBACK_IMAGE_URL

  const brandLogoUrl = isMotorcycle
    ? `https://mycle.co.kr/resources/images/car/icon_bike_${brandIcon}_l.png`
    : getCarBrandLogoUrl(brandIcon)

  const carImageUrls = [getCarHubImageUrl(brand, lowerModelName)]

  return (
    <section
      className={`flex flex-col gap-dimension-x3 ${
        isInApp ? 'pb-dimension-x2' : 'pb-dimension-x6'
      }`}
    >
      <div ref={heroRef}>
        <CarImage
          company={brand}
          modelName={upperModelName}
          brandLogoUrl={brandLogoUrl}
          carImageUrls={carImageUrls}
          fallbackImageUrl={fallbackImageUrl}
        />
      </div>

      <div
        ref={modelInfoRef}
        className='flex flex-col gap-dimension-x3 px-dimension-x5'
      >
        <div className='flex flex-col gap-dimension-x1'>
          <div className='flex items-end justify-between pl-[6px]'>
            <Text
              variant='body-1-semibold'
              color='text-foreground-neutral-alternative'
              className='min-w-0 truncate'
            >
              {brand} {upperModelName}
            </Text>
            <FuelTypeFilter
              value={fuelType}
              options={fuelOptions}
              onChange={handleFuelChange}
              onOpen={handleFuelOpen}
            />
          </div>
          <div className='flex flex-col gap-dimension-0-5'>
            <Text
              as='h1'
              variant='display-2-bold'
              color='text-foreground-neutral-strong'
              className='w-full break-keep pl-[6px] line-clamp-2'
            >
              {lowerModelName}
            </Text>
            {ownerReviewCount > 0 ? (
              <div>
                <TextButton
                  text={`오너 리뷰 ${ownerReviewCount.toLocaleString()}`}
                  variant='underline'
                  typography='body-1-semibold'
                  textColor='foreground-neutral-alternative'
                  clickable={() => onScrollTo('owner-review')}
                />
              </div>
            ) : null}
          </div>
        </div>

        {isMotorcycle || isLock ? null : (
          <RankChips
            satisfactionRank={satisfactionRank}
            efficiencyRank={efficiencyRank}
            fuelCostPercentile={fuelCostPercentile}
            fuelType={fuelType}
            segment={segment}
          />
        )}

        {isInApp ? null : (
          <AiSummaryCard
            summary={aiSummary}
            basis={detail.dataBasisDate ?? realFuel.basisYearMonth}
            hasData={realFuel.realFuelEconomy > 0 && realFuel.ownerCount > 0}
            locked={isLock}
          />
        )}
      </div>
    </section>
  )
}

export default HeroCard
